import React, { useState, useEffect } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { calendarAPI, meetingAPI, workspaceAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { cn } from '../lib/utils';
import {
    ChevronLeft,
    ChevronRight,
    Loader2,
    Plus,
    Video,
    ExternalLink,
    Trash2,
    X,
    Clock,
    Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, getDay, addDays } from 'date-fns';

const statusColors = {
    'to do': '#9FB0AC',
    'in progress': '#6EC1E4',
    'review': '#F5C26B',
    'done': '#7ED957',
    'complete': '#7ED957',
};

const CalendarPage = () => {
    const { currentWorkspace, members } = useWorkspace();
    const { user } = useAuth();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [createMeetingModal, setCreateMeetingModal] = useState(false);
    const [meetingDetailModal, setMeetingDetailModal] = useState(null);
    const [creating, setCreating] = useState(false);
    const [meetingForm, setMeetingForm] = useState({
        title: '',
        description: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        startTime: '09:00',
        endTime: '10:00',
        participant_ids: []
    });

    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    
    const startDay = getDay(monthStart);
    const calendarStart = addDays(monthStart, -startDay);
    const days = eachDayOfInterval({ start: calendarStart, end: addDays(monthEnd, 6 - getDay(monthEnd)) });

    useEffect(() => {
        if (currentWorkspace) {
            loadData();
        }
    }, [currentWorkspace, currentDate]);

    const loadData = async () => {
        if (!currentWorkspace) return;
        setLoading(true);
        try {
            const startDate = format(addDays(monthStart, -7), 'yyyy-MM-dd');
            const endDate = format(addDays(monthEnd, 7), 'yyyy-MM-dd');
            
            const [eventsRes, meetingsRes] = await Promise.all([
                calendarAPI.getEvents(currentWorkspace.id, startDate, endDate),
                meetingAPI.getAll(currentWorkspace.id, startDate + 'T00:00:00', endDate + 'T23:59:59')
            ]);
            
            setEvents(eventsRes.data);
            setMeetings(meetingsRes.data);
        } catch (error) {
            console.error('Failed to load calendar data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getEventsForDay = (day) => {
        const dayStr = format(day, 'yyyy-MM-dd');
        const dayTasks = events.filter(event => {
            const start = event.start || event.end;
            const end = event.end || event.start;
            return dayStr >= start && dayStr <= end;
        });
        
        const dayMeetings = meetings.filter(meeting => {
            const meetingDate = meeting.start_time?.split('T')[0];
            return meetingDate === dayStr;
        });
        
        return { tasks: dayTasks, meetings: dayMeetings };
    };

    const getEventColor = (event) => {
        const statusLower = event.status?.toLowerCase();
        return statusColors[statusLower] || event.color || '#2F6F63';
    };

    const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));
    const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
    const goToToday = () => setCurrentDate(new Date());

    const handleCreateMeeting = async () => {
        if (!meetingForm.title) {
            toast.error('Judul meeting harus diisi');
            return;
        }
        
        setCreating(true);
        try {
            const startTime = `${meetingForm.date}T${meetingForm.startTime}:00`;
            const endTime = `${meetingForm.date}T${meetingForm.endTime}:00`;
            
            await meetingAPI.create(currentWorkspace.id, {
                title: meetingForm.title,
                description: meetingForm.description,
                start_time: startTime,
                end_time: endTime,
                participant_ids: meetingForm.participant_ids
            });
            
            toast.success('Meeting berhasil dibuat');
            setCreateMeetingModal(false);
            setMeetingForm({
                title: '',
                description: '',
                date: format(new Date(), 'yyyy-MM-dd'),
                startTime: '09:00',
                endTime: '10:00',
                participant_ids: []
            });
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Gagal membuat meeting');
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteMeeting = async (meetingId) => {
        try {
            await meetingAPI.delete(currentWorkspace.id, meetingId);
            toast.success('Meeting berhasil dihapus');
            setMeetingDetailModal(null);
            loadData();
        } catch (error) {
            toast.error('Gagal menghapus meeting');
        }
    };

    const toggleParticipant = (userId) => {
        setMeetingForm(prev => ({
            ...prev,
            participant_ids: prev.participant_ids.includes(userId)
                ? prev.participant_ids.filter(id => id !== userId)
                : [...prev.participant_ids, userId]
        }));
    };

    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    return (
        <Layout>
            <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-heading font-bold text-brand-text-primary">Calendar</h1>
                        <p className="text-sm text-brand-text-muted">View tasks and meetings in calendar format</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button 
                            onClick={() => setCreateMeetingModal(true)}
                            className="bg-brand-primary hover:bg-brand-primary-dark"
                        >
                            <Video className="w-4 h-4 mr-2" />
                            Create Meeting
                        </Button>
                        <Button variant="outline" size="sm" onClick={goToToday}>
                            Today
                        </Button>
                        <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" onClick={prevMonth}>
                                <ChevronLeft className="w-5 h-5" />
                            </Button>
                            <span className="text-lg font-semibold min-w-[180px] text-center">
                                {format(currentDate, 'MMMM yyyy')}
                            </span>
                            <Button variant="ghost" size="icon" onClick={nextMonth}>
                                <ChevronRight className="w-5 h-5" />
                            </Button>
                        </div>
                    </div>
                </div>

                {loading && (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                    </div>
                )}

                {!loading && (
                    <Card className="border-brand-border shadow-card">
                        <CardContent className="p-0">
                            {/* Week day headers */}
                            <div className="grid grid-cols-7 border-b border-brand-border">
                                {weekDays.map(day => (
                                    <div 
                                        key={day} 
                                        className="p-3 text-center text-sm font-semibold text-brand-text-muted bg-brand-background"
                                    >
                                        {day}
                                    </div>
                                ))}
                            </div>

                            {/* Calendar grid */}
                            <div className="grid grid-cols-7">
                                {days.map((day, idx) => {
                                    const { tasks: dayTasks, meetings: dayMeetings } = getEventsForDay(day);
                                    const isToday = isSameDay(day, new Date());
                                    const isCurrentMonth = isSameMonth(day, currentDate);
                                    
                                    return (
                                        <div
                                            key={idx}
                                            className={cn(
                                                "min-h-[120px] p-2 border-b border-r border-brand-border last:border-r-0",
                                                !isCurrentMonth && "bg-gray-50 opacity-50"
                                            )}
                                        >
                                            <div className={cn(
                                                "flex items-center justify-center w-7 h-7 text-sm font-medium rounded-full mb-1",
                                                isToday && "bg-brand-primary text-white",
                                                !isToday && isCurrentMonth && "text-brand-text-primary",
                                                !isCurrentMonth && "text-brand-text-muted"
                                            )}>
                                                {format(day, 'd')}
                                            </div>
                                            
                                            {/* Meetings */}
                                            {dayMeetings.slice(0, 2).map(meeting => (
                                                <div
                                                    key={meeting.id}
                                                    onClick={() => setMeetingDetailModal(meeting)}
                                                    className="text-xs p-1 mb-1 rounded truncate cursor-pointer bg-purple-100 text-purple-700 hover:bg-purple-200 flex items-center gap-1"
                                                    title={meeting.title}
                                                >
                                                    <Video className="w-3 h-3 flex-shrink-0" />
                                                    <span className="truncate">{meeting.title}</span>
                                                </div>
                                            ))}
                                            
                                            {/* Tasks */}
                                            {dayTasks.slice(0, 2).map(event => (
                                                <div
                                                    key={event.id}
                                                    onClick={() => setSelectedEvent(event)}
                                                    className="text-xs p-1 mb-1 rounded truncate cursor-pointer hover:opacity-80"
                                                    style={{ 
                                                        backgroundColor: `${getEventColor(event)}20`,
                                                        borderLeft: `3px solid ${getEventColor(event)}`,
                                                        color: getEventColor(event)
                                                    }}
                                                    title={event.title}
                                                >
                                                    {event.title}
                                                </div>
                                            ))}
                                            
                                            {(dayTasks.length + dayMeetings.length) > 4 && (
                                                <div className="text-xs text-brand-text-muted">
                                                    +{dayTasks.length + dayMeetings.length - 4} more
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Legend */}
                <div className="mt-4 flex items-center gap-6 text-sm text-brand-text-muted">
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded bg-purple-500"></div>
                        <span>Meeting</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: statusColors['in progress'] }}></div>
                        <span>In Progress</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded" style={{ backgroundColor: statusColors['done'] }}></div>
                        <span>Done</span>
                    </div>
                </div>
            </div>

            {/* Create Meeting Modal */}
            <Dialog open={createMeetingModal} onOpenChange={setCreateMeetingModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Video className="w-5 h-5 text-purple-500" />
                            Create Meeting
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Judul Meeting *</Label>
                            <Input
                                value={meetingForm.title}
                                onChange={(e) => setMeetingForm({ ...meetingForm, title: e.target.value })}
                                placeholder="Contoh: Weekly Standup"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Deskripsi</Label>
                            <Textarea
                                value={meetingForm.description}
                                onChange={(e) => setMeetingForm({ ...meetingForm, description: e.target.value })}
                                placeholder="Agenda meeting..."
                                rows={2}
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Tanggal</Label>
                                <Input
                                    type="date"
                                    value={meetingForm.date}
                                    onChange={(e) => setMeetingForm({ ...meetingForm, date: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Waktu Mulai</Label>
                                <Input
                                    type="time"
                                    value={meetingForm.startTime}
                                    onChange={(e) => setMeetingForm({ ...meetingForm, startTime: e.target.value })}
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Waktu Selesai</Label>
                            <Input
                                type="time"
                                value={meetingForm.endTime}
                                onChange={(e) => setMeetingForm({ ...meetingForm, endTime: e.target.value })}
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Peserta</Label>
                            <div className="max-h-32 overflow-y-auto border rounded-md p-2 space-y-1">
                                {members.map(member => (
                                    <label
                                        key={member.user_id}
                                        className="flex items-center gap-2 p-1 hover:bg-gray-50 rounded cursor-pointer"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={meetingForm.participant_ids.includes(member.user_id)}
                                            onChange={() => toggleParticipant(member.user_id)}
                                            className="rounded"
                                        />
                                        <span className="text-sm">{member.user_name}</span>
                                        <span className="text-xs text-gray-400">{member.user_email}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreateMeetingModal(false)}>
                            Batal
                        </Button>
                        <Button onClick={handleCreateMeeting} disabled={creating} className="bg-purple-600 hover:bg-purple-700">
                            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Video className="w-4 h-4 mr-2" />}
                            Buat Meeting
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Meeting Detail Modal */}
            <Dialog open={!!meetingDetailModal} onOpenChange={() => setMeetingDetailModal(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Video className="w-5 h-5 text-purple-500" />
                            {meetingDetailModal?.title}
                        </DialogTitle>
                    </DialogHeader>
                    {meetingDetailModal && (
                        <div className="space-y-4 py-4">
                            {meetingDetailModal.description && (
                                <p className="text-sm text-gray-600">{meetingDetailModal.description}</p>
                            )}
                            
                            <div className="flex items-center gap-2 text-sm">
                                <Clock className="w-4 h-4 text-gray-400" />
                                <span>
                                    {format(new Date(meetingDetailModal.start_time), 'dd MMM yyyy, HH:mm')} - 
                                    {format(new Date(meetingDetailModal.end_time), 'HH:mm')}
                                </span>
                            </div>
                            
                            <div className="flex items-center gap-2 text-sm">
                                <Users className="w-4 h-4 text-gray-400" />
                                <span>{meetingDetailModal.participant_ids?.length || 0} peserta</span>
                            </div>
                            
                            <div className="p-3 bg-purple-50 rounded-lg">
                                <p className="text-xs text-purple-600 mb-2">
                                    {meetingDetailModal.meeting_provider === 'google_meet' ? 'Google Meet' : meetingDetailModal.meeting_provider === 'jitsi' ? 'Jitsi Meet' : 'Zoom Meet'}
                                </p>
                                <a 
                                    href={meetingDetailModal.meeting_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-purple-700 hover:text-purple-800 font-medium"
                                >
                                    <ExternalLink className="w-4 h-4" />
                                    Join Meeting
                                </a>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => handleDeleteMeeting(meetingDetailModal?.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Hapus
                        </Button>
                        <Button 
                            onClick={() => window.open(meetingDetailModal?.meeting_url, '_blank')}
                            className="bg-purple-600 hover:bg-purple-700"
                        >
                            <Video className="w-4 h-4 mr-2" />
                            Join Meeting
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Task Event Detail Modal */}
            <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{selectedEvent?.title}</DialogTitle>
                    </DialogHeader>
                    {selectedEvent && (
                        <div className="space-y-4 py-4">
                            <div className="flex items-center gap-2">
                                <span 
                                    className="px-2 py-1 rounded text-xs font-medium"
                                    style={{ 
                                        backgroundColor: `${getEventColor(selectedEvent)}20`,
                                        color: getEventColor(selectedEvent)
                                    }}
                                >
                                    {selectedEvent.status}
                                </span>
                                {selectedEvent.priority && (
                                    <span className="text-xs text-gray-500">
                                        Priority: {selectedEvent.priority}
                                    </span>
                                )}
                            </div>
                            {selectedEvent.start && (
                                <p className="text-sm text-gray-600">
                                    📅 {selectedEvent.start} {selectedEvent.end && selectedEvent.end !== selectedEvent.start && `- ${selectedEvent.end}`}
                                </p>
                            )}
                            {selectedEvent.task_code && (
                                <p className="text-xs text-gray-400">Task Code: {selectedEvent.task_code}</p>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedEvent(null)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
};

export default CalendarPage;
